import { z } from 'zod';
import { DOMAIN_NAME_PATTERN, SLUG_PATTERN } from './patterns.js';

export const domainNameSchema = z.string().max(253)
  .regex(DOMAIN_NAME_PATTERN)
  .refine((value) => value.trim() === value);
export const createSiteSchema = z.object({
  slug: z.string().min(1).max(63).regex(SLUG_PATTERN).refine((value) => value.trim() === value),
  displayName: z.string().trim().min(1).max(160),
  primaryDomain: domainNameSchema,
  aliases: z.array(domainNameSchema).max(100).default([]),
  templateVersionId: z.string().uuid(),
  nodeId: z.string().uuid().optional(),
  tier: z.enum(['production', 'staging']).default('staging'),
  backupPolicy: z.enum(['standard', 'active', 'critical']).default('standard'),
  adminLogin: z.string().min(3).max(60).regex(/^[a-zA-Z0-9_.-]+$/).default('siteadmin'),
  adminEmail: z.string().email().max(254),
}).strict().refine((value) => new Set([value.primaryDomain, ...value.aliases]).size === value.aliases.length + 1,
  'Domain names must be distinct');

/** What the server works with once parsed, defaults applied; `…Request` types are what a client sends. */
export type CreateSiteInput = z.infer<typeof createSiteSchema>;
export type CreateSiteRequest = z.input<typeof createSiteSchema>;

/**
 * An existing WordPress brought under management (P7): the new site's own
 * settings; its content and its administrators come from the old server.
 */
export const importSiteSchema = z.object({
  slug: z.string().min(1).max(63).regex(SLUG_PATTERN).refine((value) => value.trim() === value),
  displayName: z.string().trim().min(1).max(160),
  primaryDomain: domainNameSchema,
  aliases: z.array(domainNameSchema).max(100).default([]),
  templateVersionId: z.string().uuid(),
  nodeId: z.string().uuid().optional(),
  tier: z.enum(['production', 'staging']).default('production'),
  backupPolicy: z.enum(['standard', 'active', 'critical']).default('standard'),
}).strict().refine((value) => new Set([value.primaryDomain, ...value.aliases]).size === value.aliases.length + 1,
  'Domain names must be distinct');
export type ImportSiteRequest = z.input<typeof importSiteSchema>;

/** What the old server says about itself when its upload is complete. */
export const importMetaSchema = z.object({
  tablePrefix: z.string().regex(/^[A-Za-z0-9_]{1,32}$/),
  wpVersion: z.string().max(40).regex(/^[0-9A-Za-z.+-]*$/).nullable().optional(),
  phpVersion: z.string().max(40).regex(/^[0-9A-Za-z.+~-]*$/).nullable().optional(),
  siteUrl: z.string().max(500).nullable().optional(),
  rootFiles: z.array(z.string().max(255)).max(200).default([]),
  multisite: z.boolean().default(false),
}).strict();
export type ImportMeta = z.infer<typeof importMetaSchema>;

/**
 * A site the panel watches but does not run (S7): an existing site on its own
 * server, before it moves, or one that never will. Checked like any other;
 * nothing is ever done to it.
 */
export const observeSiteSchema = z.object({
  displayName: z.string().trim().min(1).max(160),
  primaryDomain: domainNameSchema,
  aliases: z.array(domainNameSchema).max(20).default([]),
}).strict().refine((value) => new Set([value.primaryDomain, ...value.aliases]).size === value.aliases.length + 1,
  'Domain names must be distinct');

export type ObserveSiteInput = z.infer<typeof observeSiteSchema>;
export type ObserveSiteRequest = z.input<typeof observeSiteSchema>;

/**
 * What a clone becomes (ADR-0038): a test copy, closed to search engines, or a
 * new working site made on the source's model — for another country, another
 * language, another line of business.
 */
export const CLONE_PURPOSES = ['staging', 'production'] as const;
export type ClonePurpose = (typeof CLONE_PURPOSES)[number];

/** A country as a site records it: ISO 3166-1 alpha-2 — DE, JP, BR. */
export const COUNTRY_CODE = /^[A-Z]{2}$/;
/** A language as a site records it: a BCP 47 tag — de, ja, pt-BR, zh-Hant. */
export const LANGUAGE_TAG = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

/**
 * A clone of an existing site (TZ section 10.4). Only what differs from the
 * source is asked for: the template, the server and what the site runs on come
 * with it. A site the panel runs also needs an identifier and the
 * administrator the copy is handed over with; a site of an external server
 * may be put on another connected server.
 */
export const cloneSiteSchema = z.object({
  mode: z.enum(CLONE_PURPOSES).default('staging'),
  displayName: z.string().trim().min(1).max(160),
  primaryDomain: domainNameSchema,
  slug: createSiteSchema.innerType().shape.slug.optional(),
  adminLogin: z.string().min(3).max(60).regex(/^[a-zA-Z0-9_.-]+$/).default('siteadmin'),
  adminEmail: z.string().email().max(254).optional(),
  targetNodeId: z.string().uuid().optional(),
  country: z.string().regex(COUNTRY_CODE).optional(),
  language: z.string().regex(LANGUAGE_TAG).optional(),
  /**
   * Rid the copy of its source's verification codes, counters, hreflang and
   * foreign canonicals (ADR-0041). Unsaid: yes for a new site, no for a test copy.
   */
  cleanTraces: z.boolean().optional(),
  /**
   * For a site of an external server: the directory the copy goes in
   * (ADR-0043). Unsaid, it follows how the server lays its sites out.
   */
  targetRoot: z.string().max(300).regex(/^\/[A-Za-z0-9._\-/]+$/).refine((dir) => !dir.split('/').includes('..'), 'A plain absolute directory').optional(),
}).strict();

export type CloneSiteInput = z.infer<typeof cloneSiteSchema>;

/**
 * A new site of files on an external server (ADR-0044): no CMS, whoever
 * makes its pages. Its files are an uploaded archive, or a starter page until
 * they come; its directory follows how the server lays its sites out unless
 * one is given.
 */
export const createFilesSiteSchema = z.object({
  displayName: z.string().trim().min(1).max(160),
  primaryDomain: domainNameSchema,
  /** Answer for the www form too. */
  www: z.boolean().default(true),
  hostNodeId: z.string().uuid(),
  tier: z.enum(CLONE_PURPOSES).default('production'),
  /** An archive uploaded before (`PUT /site-uploads/:id`); none: a starter page. */
  uploadId: z.string().uuid().optional(),
  targetRoot: z.string().max(300).regex(/^\/[A-Za-z0-9._\-/]+$/).refine((dir) => !dir.split('/').includes('..'), 'A plain absolute directory').optional(),
  country: z.string().regex(COUNTRY_CODE).optional(),
  language: z.string().regex(LANGUAGE_TAG).optional(),
}).strict();
export type CreateFilesSiteInput = z.infer<typeof createFilesSiteSchema>;
export type CreateFilesSiteRequest = z.input<typeof createFilesSiteSchema>;
export type CloneSiteRequest = z.input<typeof cloneSiteSchema>;

/**
 * Moving a site to another node. The destination may be left to the scheduler;
 * the confirmation is the site's name, typed, because the site goes into
 * maintenance for the length of the move.
 */
export const migrateSiteSchema = z.object({
  targetNodeId: z.string().uuid().optional(),
  confirmation: z.string().min(1).max(253),
}).strict();
export type MigrateSiteRequest = z.input<typeof migrateSiteSchema>;

/** Raising a site whose node is gone on another, from a backup the panel holds (P8). */
export const relocateSiteSchema = z.object({
  backupId: z.string().uuid(),
  targetNodeId: z.string().uuid().optional(),
  confirmation: z.string().min(1).max(253),
}).strict();
export type RelocateSiteRequest = z.input<typeof relocateSiteSchema>;

export const siteCommandSchema = z.discriminatedUnion('operation', [
  z.object({ operation: z.literal('deploy') }).strict(),
  z.object({ operation: z.literal('restart') }).strict(),
  z.object({ operation: z.literal('backup') }).strict(),
  // Restoring replaces what the site holds now, so it is confirmed the same way
  // a destroy is: by typing the site's name.
  z.object({ operation: z.literal('restore'), backupId: z.string().uuid(), confirmation: z.string().min(1).max(253) }).strict(),
  z.object({ operation: z.literal('destroy'), confirmation: z.string().min(1).max(253), revision: z.number().int().positive() }).strict(),
  // Another template version, or the same one to bring the site onto the
  // current runtime (S4). A backup is taken first and the update is undone
  // if the site does not answer after it.
  z.object({ operation: z.literal('upgrade'), templateVersionId: z.string().uuid() }).strict(),
  // The site's names as they should be (S6): which one is primary and which
  // are aliases. Added names are pointed at the site's node, removed ones
  // released, and a new primary becomes the address WordPress answers with.
  z.object({
    operation: z.literal('domains'),
    primaryDomain: domainNameSchema,
    aliases: z.array(domainNameSchema).max(20),
  }).strict(),
  // The template's structure applied again (ADR-0047); `open` also lets search
  // engines in — how a new GEO is launched once a person has looked at it.
  z.object({ operation: z.literal('structure'), indexing: z.enum(['open', 'closed']).optional() }).strict(),
]);
export type SiteCommand = z.infer<typeof siteCommandSchema>;
export type SiteCommandRequest = z.input<typeof siteCommandSchema>;

/**
 * A new GEO (ADR-0047): a template build, a market and a domain — the rest is
 * derived. `www` adds the www name when the domain is a registered one.
 */
export const createGeoSiteSchema = z.object({
  templateId: z.string().uuid(),
  market: z.string().min(2).max(10).regex(/^[a-z]{2}(?:-[a-z]{2})?$/),
  primaryDomain: domainNameSchema,
  www: z.boolean().default(false),
  nodeId: z.string().uuid().optional(),
  displayName: z.string().trim().min(1).max(160).optional(),
}).strict();
export type CreateGeoSiteRequest = z.input<typeof createGeoSiteSchema>;

/** A template taken from its reference site (ADR-0047): a new template, or the next version of one. */
export const captureTemplateSchema = z.object({
  templateId: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(100).optional(),
}).strict();
export type CaptureTemplateRequest = z.input<typeof captureTemplateSchema>;

/** A site's translations of its template's structure strings, by key; an empty one goes back to the template's. */
export const structureStringsSchema = z.object({
  strings: z.record(z.string().max(300), z.string().max(1000)),
}).strict();
export type StructureStringsRequest = z.input<typeof structureStringsSchema>;

/** A template version rolled out to the template's sites that are behind it. */
export const rolloutSchema = z.object({ versionId: z.string().uuid() }).strict();
